import { useDeferredValue, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Copy, Check, Plus, Upload } from 'lucide-react';
import { toast } from 'sonner';

import { PageHeader } from '@/components/page-header';
import { RouteBreadcrumb } from '@/components/route-breadcrumb';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ErrorNotice } from '@/components/ui/error-notice';

import type { AttributeNode } from '@/types/schemas';
import type { SchemaRecord, SchemaStatus } from '@/domain/entities/property-schema';

import { useAppDispatch, useAppSelector } from '@/store';
import {
  cloneSchema,
  createSchema,
  deprecateSchema,
  fetchSchemaById,
  updateSchema,
} from '@/features/property-schemas/schemasThunks';
import { selectSchemaByPkSk } from '@/features/property-schemas/schemasSlice';
import { useConfirmDialog } from '@/hooks/useConfirmDialog';

import { AttributeEditor } from './components/AttributeEditor';
import {
  nowUnixSeconds,
  makeEmptyBlock,
  buildRootSchema,
  schemaToBlocks,
  dedupeAndValidateKeys,
  validateTree,
  validateSchemaPayload,
} from './utils/schema-utils';

export default function AddSchemaBuilderPage() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const confirm = useConfirmDialog();
  const { schemaId, version } = useParams<{ schemaId: string; version?: string }>();
  const schemaFromStore = useAppSelector((s) => selectSchemaByPkSk(s, schemaId, version));

  const isViewingSnapshot = Boolean(version);
  const isReadOnly = isViewingSnapshot;
  const isEditHead = Boolean(schemaFromStore) && !isViewingSnapshot;

  const initial = useMemo(() => {
    const ts = nowUnixSeconds();
    return {
      meta: {
        id: 'SCHEMA#new',
        version: 'HEAD',
        schemaVersion: 1,
        status: 'draft' as SchemaStatus,
        title: '',
        description: '',
        createdAt: ts,
        updatedAt: ts,
      },
      blocks: [] as AttributeNode[],
    };
  }, []);

  const [meta, setMeta] = useState(initial.meta);
  const [blocks, setBlocks] = useState<AttributeNode[]>(initial.blocks);
  const [copied, setCopied] = useState(false);
  const [isHydratingSchema, setIsHydratingSchema] = useState(false);
  const [defaultBlocksOpen, setDefaultBlocksOpen] = useState(!schemaFromStore);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [importPayload, setImportPayload] = useState('');
  const [importError, setImportError] = useState<string | null>(null);
  const breadcrumbLabel = schemaFromStore?.title?.trim() || (schemaId ? meta.title.trim() || schemaId : 'New Schema');

  useEffect(() => {
    if (!schemaId || schemaFromStore) return;
    void dispatch(fetchSchemaById({ id: schemaId, version: version ?? 'HEAD' }))
      .unwrap()
      .catch(() => {
        // fetch errors are handled in slice state; keep page side effects quiet
      });
  }, [dispatch, schemaFromStore, schemaId, version]);

  useEffect(() => {
    if (!schemaFromStore) return;

    setIsHydratingSchema(true);

    const nextMeta = {
      id: schemaFromStore.id,
      version: schemaFromStore.version,
      schemaVersion: schemaFromStore.schemaVersion ?? 1,
      status: schemaFromStore.status,
      title: schemaFromStore.title,
      description: schemaFromStore.description ?? '',
      createdAt: schemaFromStore.createdAt || nowUnixSeconds(),
      updatedAt: schemaFromStore.updatedAt || nowUnixSeconds(),
    };

    const root = schemaFromStore.jsonSchema;
    const frame = window.requestAnimationFrame(() => {
      setMeta(nextMeta);
      const validation = validateSchemaPayload(root);
      setBlocks(validation.ok ? schemaToBlocks(validation.schema) : []);
      setDefaultBlocksOpen(false);
      setIsHydratingSchema(false);
    });

    return () => {
      window.cancelAnimationFrame(frame);
      setIsHydratingSchema(false);
    };
  }, [schemaFromStore, schemaId, version]);

  const updateMeta = (patch: Partial<typeof meta>) => {
    setMeta((prev) => ({ ...prev, ...patch, updatedAt: nowUnixSeconds() }));
  };

  const addBlock = () => {
    setDefaultBlocksOpen(true);
    setBlocks((prev) => [...prev, makeEmptyBlock()]);
    updateMeta({});
  };

  const updateBlockAt = (index: number, next: AttributeNode) => {
    setBlocks((prev) => {
      const copy = [...prev];
      copy[index] = next;
      return copy;
    });
    updateMeta({});
  };

  const removeBlockAt = (index: number) => {
    setBlocks((prev) => prev.filter((_, i) => i !== index));
    updateMeta({});
  };

  const buildRecord = (sourceMeta = meta, sourceBlocks = blocks): SchemaRecord => ({
    id: sourceMeta.id,
    version: sourceMeta.version,
    schemaVersion: sourceMeta.schemaVersion,
    status: sourceMeta.status,
    typeName: 'Schema',
    title: sourceMeta.title,
    description: sourceMeta.description,
    createdAt: sourceMeta.createdAt,
    updatedAt: sourceMeta.updatedAt,
    jsonSchema: buildRootSchema(sourceBlocks),
  });

  const deferredMeta = useDeferredValue(meta);
  const deferredBlocks = useDeferredValue(blocks);
  const previewRecord: SchemaRecord = useMemo(
    () => ({
      id: deferredMeta.id,
      version: deferredMeta.version,
      schemaVersion: deferredMeta.schemaVersion,
      status: deferredMeta.status,
      typeName: 'Schema',
      title: deferredMeta.title,
      description: deferredMeta.description,
      createdAt: deferredMeta.createdAt,
      updatedAt: deferredMeta.updatedAt,
      jsonSchema: buildRootSchema(deferredBlocks),
    }),
    [deferredMeta, deferredBlocks],
  );
  const previewJson = useMemo(() => JSON.stringify(previewRecord, null, 2), [previewRecord]);

  const copyToClipboard = async () => {
    const record = buildRecord();
    await navigator.clipboard.writeText(JSON.stringify(record, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const applyImportedPayload = () => {
    setImportError(null);

    let parsed: unknown;
    try {
      parsed = JSON.parse(importPayload);
    } catch {
      setImportError('Payload must be valid JSON.');
      return;
    }

    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      setImportError('Payload must be a JSON object.');
      return;
    }

    const validation = validateSchemaPayload(parsed);
    if (!validation.ok) {
      setImportError(validation.errors.join('\n'));
      return;
    }

    const payload = parsed as Partial<SchemaRecord> & { jsonSchema?: unknown };
    const nextMeta: Partial<typeof meta> = {
      updatedAt: nowUnixSeconds(),
    };

    if (typeof payload.title === 'string') {
      const title = payload.title.trim();
      nextMeta.title = `${title || 'Schema'}_cloned`;
    }
    if (typeof payload.description === 'string' || payload.description === null) {
      nextMeta.description = payload.description ?? '';
    }
    setMeta((previous) => ({
      ...previous,
      ...nextMeta,
    }));
    setBlocks(schemaToBlocks(validation.schema));
    setDefaultBlocksOpen(false);
    setImportPayload('');
    setIsImportDialogOpen(false);
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isReadOnly) return;

    if (!meta.title.trim()) return alert('Title is required.');
    if (!meta.id.trim()) return alert('ID is required.');
    if (!meta.version.trim()) return alert('Version is required.');

    const rootRes = dedupeAndValidateKeys(blocks);
    if (!rootRes.ok) return alert(rootRes.error);

    for (const b of blocks) {
      const res = validateTree(b, 'root');
      if (!res.ok) return alert(res.error);
    }

    const nextRecord = buildRecord();
    const payload: SchemaRecord = {
      ...nextRecord,
      description: nextRecord.description?.trim() ? nextRecord.description : null,
    };

    const promise = (async () => {
      if (isEditHead) {
        await dispatch(updateSchema(payload)).unwrap();
        return { id: payload.id };
      }

      const createPayload = {
        title: payload.title,
        description: payload.description,
        jsonSchema: payload.jsonSchema,
      };

      const res = await dispatch(createSchema(createPayload)).unwrap();
      const newId = (res.data as SchemaRecord | undefined)?.id;

      if (!newId) throw new Error('Create succeeded but no schema id was returned from the API.');
      return { id: newId };
    })();

    toast.promise(promise, {
      loading: isEditHead ? 'Updating schema…' : 'Creating schema…',
      success: isEditHead ? 'Schema updated successfully' : 'Schema created successfully',
      error: (err) => err?.message || (isEditHead ? 'Failed to update schema' : 'Failed to create schema'),
    });

    try {
      const { id } = await promise;
      if (!isEditHead) navigate(`/admin/schemas/${encodeURIComponent(id)}/edit`, { replace: true });
    } catch {
      // toast already handled
    }
  };

  const handleClone = async () => {
    if (!schemaFromStore) return;
    if (!isViewingSnapshot) return;

    const ok = await confirm({
      title: `Clone schema ${schemaFromStore.version}?`,
      description: 'This will create a new draft (HEAD).',
      confirmLabel: 'Clone',
    });
    if (!ok) return;

    const promise = (async () => {
      const res = await dispatch(cloneSchema({ id: schemaFromStore.id, version: schemaFromStore.version })).unwrap();
      const newRec = res.data as SchemaRecord | undefined;
      const newId = newRec?.id;
      if (!newId) throw new Error('Clone succeeded but API did not return the new schema id.');
      return { id: newId };
    })();

    toast.promise(promise, {
      loading: 'Cloning schema…',
      success: 'Schema cloned successfully',
      error: (err) => err?.message || 'Failed to clone schema',
    });

    try {
      const { id } = await promise;
      navigate(`/admin/schemas/${encodeURIComponent(id)}/edit`, { replace: true });
    } catch {
      // toast already handled
    }
  };

  const handleDeprecate = async () => {
    if (!schemaFromStore || !isViewingSnapshot || schemaFromStore.status === 'deprecated') return;

    const ok = await confirm({
      title: 'Deprecate this schema?',
      description: 'This will prevent future use but keep it for historical records.',
      confirmLabel: 'Deprecate',
      variant: 'destructive',
    });
    if (!ok) return;

    const promise = dispatch(deprecateSchema({ id: schemaFromStore.id, version: schemaFromStore.version })).unwrap();

    toast.promise(promise, {
      loading: 'Deprecating schema…',
      success: 'Schema deprecated',
      error: (err) => err?.message || 'Failed to deprecate schema',
    });

    try {
      await promise;
      navigate('/admin/schemas', { replace: true });
    } catch {
      // toast already handled
    }
  };

  return (
    <div className="space-y-6">
      <RouteBreadcrumb items={[{ label: 'Schemas', to: '/admin/schemas' }, { label: breadcrumbLabel }]} />

      <PageHeader
        title="Schema Builder"
        description="Generic recursive builder using attributes (no schema defaults)."
      />

      {!isReadOnly ? (
        <div className="flex justify-end">
          <Button type="submit" form="schema-builder-form" size="lg" className="min-w-[180px]">
            {isEditHead ? 'Update Schema' : 'Save Schema'}
          </Button>
        </div>
      ) : null}

      {isReadOnly && schemaFromStore ? (
        <div className="flex items-center justify-end gap-2">
          <Button type="button" variant="outline" onClick={handleClone}>
            Clone
          </Button>

          {schemaFromStore.status !== 'deprecated' ? (
            <Button type="button" variant="destructive" onClick={handleDeprecate}>
              Deprecate
            </Button>
          ) : null}
        </div>
      ) : null}

      {!isReadOnly ? (
        <Card className="rounded-2xl">
          <CardHeader className="flex flex-row items-center justify-between gap-4">
            <div>
              <CardTitle>Create from Payload</CardTitle>
              <CardDescription>
                Paste a schema payload once, validate it, then hydrate the builder UI from it.
              </CardDescription>
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setImportError(null);
                setIsImportDialogOpen(true);
              }}
            >
              <Upload className="mr-2 h-4 w-4" />
              Import Payload
            </Button>
          </CardHeader>
        </Card>
      ) : null}

      <form id="schema-builder-form" onSubmit={onSubmit} className="space-y-6">
        {/* Metadata */}
        <Card className="rounded-2xl">
          <CardHeader>
            <CardTitle>Schema Metadata</CardTitle>
            <CardDescription>Wrapper fields stored alongside the schema definition.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Title</Label>
                <Input
                  value={meta.title}
                  onChange={(e) => updateMeta({ title: e.target.value })}
                  placeholder="e.g., Standard Residential Home"
                  disabled={isReadOnly}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={meta.description}
                onChange={(e) => updateMeta({ description: e.target.value })}
                rows={4}
                className="resize-none"
                placeholder="Describe what this schema is used for"
              />
            </div>

            <div className="grid gap-4 md:grid-cols-12">
              <div className="space-y-2 md:col-span-6">
                <Label>ID</Label>
                <Input value={meta.id} onChange={(e) => updateMeta({ id: e.target.value })} disabled />
              </div>
              <div className="space-y-2 md:col-span-3">
                <Label>Version</Label>
                <Input value={meta.version} disabled />
              </div>
              <div className="space-y-2 md:col-span-3">
                <Label>Schema Version</Label>
                <Input
                  type="number"
                  value={meta.schemaVersion}
                  onChange={(e) => updateMeta({ schemaVersion: Number(e.target.value || 1) })}
                  disabled
                />
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>createdAt</Label>
                <Input type="number" value={meta.createdAt} disabled />
              </div>
              <div className="space-y-2">
                <Label>updatedAt (unix seconds)</Label>
                <Input type="number" value={meta.updatedAt} disabled />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Blocks */}
        <Card className="rounded-2xl">
          <CardHeader className="flex flex-row items-center justify-between gap-4">
            <div>
              <CardTitle>Blocks</CardTitle>
              <CardDescription>
                Add blocks (top-level attributes). Each block can nest attributes recursively.
              </CardDescription>
            </div>
            <Button type="button" variant="secondary" onClick={addBlock} disabled={isReadOnly}>
              <Plus className="mr-2 h-4 w-4" />
              Add block
            </Button>
          </CardHeader>

          <CardContent className="space-y-4">
            <div className="text-muted-foreground text-sm">
              Required blocks output:{' '}
              <span className="font-medium">
                {JSON.stringify(blocks.filter((b) => b.required && b.key.trim()).map((b) => b.key.trim()))}
              </span>
            </div>

            {blocks.length === 0 ? (
              <div className="text-muted-foreground rounded-xl border p-4 text-sm">
                No blocks yet. Click <span className="font-medium">Add block</span> to start.
              </div>
            ) : null}
          </CardContent>
        </Card>

        <Card className="rounded-2xl">
          <CardContent className="space-y-4">
            <div className="space-y-4">
              {blocks.map((b, idx) => (
                <AttributeEditor
                  key={b.id}
                  node={b}
                  depth={0}
                  collapsible
                  defaultOpen={defaultBlocksOpen}
                  onChange={(next) => updateBlockAt(idx, next)}
                  onRemove={() => removeBlockAt(idx)}
                  disabled={isReadOnly}
                />
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Preview */}
        <Card className="relative rounded-2xl">
          <CardHeader>
            <CardTitle>Payload Preview</CardTitle>
            <CardDescription>
              This is the record you’ll store. Schema definition uses <span className="font-mono">attributes</span> (not
              properties).
            </CardDescription>
          </CardHeader>

          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={copyToClipboard}
            aria-label="Copy JSON to clipboard"
            className="absolute top-4 right-4"
          >
            {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
          </Button>

          <CardContent>
            {isHydratingSchema ? (
              <div className="text-muted-foreground rounded-xl border p-3 text-sm">Preparing schema preview…</div>
            ) : (
              <pre className="bg-muted max-h-96 overflow-auto rounded-xl border p-3 text-xs">{previewJson}</pre>
            )}
          </CardContent>
        </Card>

        {/* Submit */}
        <div className="flex justify-end pt-2">
          {!isReadOnly ? (
            <Button type="submit" size="lg" className="min-w-[250px]">
              {isEditHead ? 'Update Schema' : 'Save Schema'}
            </Button>
          ) : null}
        </div>
      </form>

      <Dialog open={isImportDialogOpen} onOpenChange={setIsImportDialogOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Import Schema Payload</DialogTitle>
            <DialogDescription>
              Paste a full schema record, a create payload with <span className="font-mono">jsonSchema</span>, or the
              root schema object itself. The builder will populate from the payload after validation.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <ErrorNotice
              className="[&_p:last-child]:whitespace-pre-line"
              message={importError}
              title="Invalid Payload"
            />
            <Textarea
              value={importPayload}
              onChange={(event) => {
                setImportPayload(event.target.value);
                setImportError(null);
              }}
              rows={18}
              className="max-h-[60vh] min-h-[360px] font-mono text-xs"
              placeholder='{"title":"Residential Schema","jsonSchema":{"type":"object","attributes":{}}}'
            />
            <p className="text-muted-foreground text-xs">
              Importing replaces the current blocks. Schema id, version, and schemaVersion are preserved.
            </p>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setIsImportDialogOpen(false);
                setImportError(null);
              }}
            >
              Cancel
            </Button>
            <Button type="button" onClick={applyImportedPayload} disabled={!importPayload.trim()}>
              Apply Payload
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
